// Pure logic for the Whole Post composer's account-aware markup section:
// what a user vs bot account can do, the clickable-CTA builder, and the
// keep/replace markup decision. React-free so it can be unit-tested directly.
//
// The backend is the authority: `account_type` comes from Telegram's own `bot`
// flag on the authenticated identity (never the label), and whole_post.py
// re-validates everything atomically before any editMessage.

export const CTA_PRESETS = ["JOIN VIP", "FREE VIP", "CONTACT ADMIN", "OPEN ACCOUNT", "GET SIGNALS", "WEBSITE"];

// Mirrors whole_post.normalize_url so the operator sees the exact destination
// the server will use before anything is sent. Returns {url} or {error}.
export const normalizeTarget = (value) => {
  const v = (value || "").trim();
  if (!v) return { error: "Enter a link, @username or t.me link." };
  let candidate = v;
  if (/^@[A-Za-z][A-Za-z0-9_]{3,31}$/.test(v)) candidate = `https://t.me/${v.slice(1)}`;
  else if (/^t\.me\//i.test(v)) candidate = `https://${v}`;
  else if (/^[A-Za-z][A-Za-z0-9_]{3,31}$/.test(v)) candidate = `https://t.me/${v}`;
  if (candidate.length > 2048 || /[\s\x00-\x1f\\]/.test(candidate)) return { error: "Link contains spaces or control characters." };
  let url;
  try { url = new URL(candidate); } catch { return { error: "Not a valid URL. Use https://…, @username or t.me/…" }; }
  if (url.username || url.password) return { error: "Links with embedded credentials are not allowed." };
  if ((url.protocol === "http:" || url.protocol === "https:") && url.hostname.includes(".")) return { url: candidate };
  if (url.protocol === "tg:" && ["resolve", "join", "user"].includes(url.hostname) && url.search) return { url: candidate };
  return { error: "Use an HTTP(S) URL, @username, t.me link, or supported tg:// link." };
};

// Capabilities the composer may offer. Unknown type -> treat as user (safe side):
// nothing is offered that could silently fail on Telegram.
export const capabilitiesFor = (accountType) => {
  const bot = accountType === "bot";
  return {
    type: accountType || "unknown",
    richText: true,
    image: true,
    caption: true,
    textLinks: true,
    inlineButtons: bot,
    notice: bot ? null : "Inline buttons require a bot account. Use a clickable caption link instead.",
  };
};

// Builds the CTA line and the entities that make its display text clickable.
// Offsets are UTF-16 units: JavaScript string lengths already are, which is
// exactly what Telegram (and whole_post.entities_for) expect — emoji count as 2.
export const buildCta = ({ display, target, emoji = "🔥", bold = true }) => {
  const label = (display || "").trim();
  if (!label) return { error: "Enter the clickable display text." };
  if (label.length > 64) return { error: "Keep the display text under 64 characters." };
  const dest = normalizeTarget(target);
  if (dest.error) return { error: dest.error };
  const prefix = emoji ? `${emoji} ` : "";
  const line = prefix + label;
  const entities = [{ type: "link", offset: prefix.length, length: label.length, url: dest.url }];
  if (bold) entities.push({ type: "bold", offset: prefix.length, length: label.length });
  return { line, entities, url: dest.url };
};

// Appends a CTA line to existing content, shifting the new entities to the end.
export const appendCta = (content, cta) => {
  const built = buildCta(cta);
  if (built.error) return { error: built.error };
  const separator = content.text ? (content.text.endsWith("\n") ? "\n" : "\n\n") : "";
  const base = content.text + separator;
  const shifted = built.entities.map((e) => ({ ...e, offset: base.length + e.offset }));
  return { text: base + built.line, entities: [...(content.entities || []), ...shifted], url: built.url };
};

// Which markup mode the draft should send, and what to tell the operator.
//   keep    -> reply_markup omitted; existing buttons untouched (any account)
//   replace -> send the draft's buttons (bot only)
export const resolveButtonsMode = ({ accountType, existingButtons, draftButtons, explicit }) => {
  const hasExisting = (existingButtons || []).flat().length > 0;
  const hasDraft = (draftButtons || []).flat().length > 0;
  if (explicit) return explicit;
  return hasDraft ? "replace" : "keep";
};

export const markupNotice = ({ accountType, existingButtons, mode }) => {
  const hasExisting = (existingButtons || []).flat().length > 0;
  if (!hasExisting) return null;
  if (mode === "keep") return "EXISTING BUTTONS WILL REMAIN UNCHANGED";
  if (accountType !== "bot") return "INLINE_BUTTON_REQUIRES_BOT — changing existing buttons needs a bot account; they will remain unchanged.";
  return "Existing buttons will be replaced by the rows below.";
};
